import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from 'axios';
import Cookies from 'js-cookie';

import Topbar from "../components/topbar";
import XeroSyncButton from "../components/xero-sync-button";
import AddInvoicePopup, { InvoiceData } from "../components/popup/add-invoice";
import InvoicePreview from "../components/popup/invoice-preview";
import TransactionDetail from "../components/popup/transaction-detail";
import InvoiceShareLink from "../components/popup/invoice-share-link";
import ImportInvoicePopup from "../components/popup/import-invoice";
import AddBtn from "../components/ui/add-btn";
import HeaderSearch from "../components/ui/header-search";
import DeletePopup from "../components/popup/delete";
import DeletePopupMobile from "../components/popup/mobile/delete-mobile";
import { FileDetails } from "../pages/transactions";
import staffImage from '../files/images/staff.png';
import DynamicTableVirtual, { AdditionalAction } from "../components/table-virtual";
import DescriptionFilter, { DescriptionFilterHandle } from "../components/filters/description-filter";
import SimpleDateFilter, { SimpleDateFilterHandle } from "../components/filters/simple-date-filter";
import AmountFilter, { AmountFilterHandle } from "../components/filters/amount-filter";
import StatusFilter, { StatusFilterHandle } from "../components/filters/status-filter";
import InvoiceLinkFilter from "../components/filters/invoice-link-filter";
import BottomSheetDialog from "../components/popup/mobile/mobile-bottom-sheet";
import { Box, Button, Checkbox, FormControlLabel, Tooltip, Typography, useMediaQuery, useTheme, Fab, Menu as MuiMenu, MenuItem, Collapse, IconButton, Paper, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Upload, ExternalLink, MailCheck, Menu, Plus, Settings, Share2, SlidersHorizontal, ChevronDown, ChevronUp, X, Link } from "lucide-react";
import InvoiceChart, { InvoicePeriodRow, PeriodGrain, rollUpPeriods } from "../components/invoice-chart";
import SidebarMobileDrawer from "../components/sidebar-mobile-drawer";
import { useSidebarMobile } from "../providers/sidebar-mobile-context";

import useApi from "../hooks/use-api";
import routes from "../api/routes";
import buildApiRequestConfig from "../api/url-builder";
import { useUser } from "../providers/user-provider";
import { useToast } from "../providers/toaster-provider";
import { DropdownOption } from "../components/ui/dropdown";
import { useSettings } from "../providers/settings-provider";
import { usePermissions } from "../providers/permissions-provider";
import { useFeatures } from "../providers/features-provider";
import '../util/string-prototype';
import { formatInvoiceStatusLabel, XERO_HIDDEN_BY_DEFAULT } from '../util/xero-invoice-status';
import { openPlanPrompt } from '../util/plan-required';

const todayPlusYears = (years: number) => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    return d;
};

const Invoice: React.FC = () => {
    const { user } = useUser();
    const { can } = usePermissions();
    // Strict check - invoice emails stay hidden unless the
    // invoice_email_sending flag is explicitly on for this entity.
    const { isVisibleInNav } = useFeatures();
    const canSendEmail = isVisibleInNav('invoice_email_sending');
    // Share / Permissions needs the Documents add-on (express-server
    // documents-addon-service.js).
    const hasDocumentsAddon = isVisibleInNav('documents');
    const canCreate = can('invoices', 'create');
    const canUpdate = can('invoices', 'update');
    const canDelete = can('invoices', 'delete');
    const { formatDate, formatAmount } = useSettings();
    const { showError, showSuccess } = useToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const DeleteModal = isMobile ? DeletePopupMobile : DeletePopup;
    const { sidebarOpen, setSidebarOpen } = useSidebarMobile();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [openModal, setOpenModal] = React.useState<string>('');
    const [fabMenuAnchor, setFabMenuAnchor] = React.useState<null | HTMLElement>(null);
    const [previewInvoiceId, setPreviewInvoiceId] = React.useState<string | null>(null);
    const [nextInvoiceNumber, setNextInvoiceNumber] = React.useState<string>('00000001');
    const [provisionDateOffset, setProvisionDateOffset] = React.useState<number>(0);
    const [termsAndConditions, setTermsAndConditions] = React.useState<string>('');
    const [selectedInvoiceId, setSelectedInvoiceId] = React.useState<number | null>(null);
    const [pendingSendEmailOnUpdate, setPendingSendEmailOnUpdate] = React.useState(false);
    const [shareInvoice, setShareInvoice] = React.useState<{ id: number; invoiceNumber: string } | null>(null);
    // Drives TransactionDetail below with autoOpenLinkModal - opens straight
    // to Find Matching Transactions for the invoice's provision, looked up by
    // invoiceNumber alone (TransactionDetail already supports this - same
    // lookup used by the "Generated from INVOICE #" link elsewhere).
    const [linkInvoiceNumber, setLinkInvoiceNumber] = React.useState<string | null>(null);
    // Separate from linkInvoiceNumber - autoOpenLinkModal must flip back to
    // false once the auto-opened sub-popup closes (same pattern as
    // transactions-virtual.tsx's own autoOpenLinkModal state), or the effect
    // that opens it would immediately reopen it the instant the sub-popup
    // falls back to the detail view. Only the outer TransactionDetail's own
    // onClose (the X button) should actually clear linkInvoiceNumber.
    const [autoOpenLinkModal, setAutoOpenLinkModal] = React.useState(false);
    const [statuses, setStatuses] = React.useState<DropdownOption[]>([]);
    // Per-status count/total for the Status column's Show/Hide popover (see
    // statusBreakdown in invoice-controller.js) - keyed by status id string.
    const [statusBreakdown, setStatusBreakdown] = React.useState<Record<string, { count: number; totalAmount: number }>>({});
    // Invoice count/total per month across every invoice the current filters
    // match (periodBreakdown in invoice-controller.js), for the chart above the table.
    const [periodBreakdown, setPeriodBreakdown] = React.useState<InvoicePeriodRow[]>([]);
    const [periodGrain, setPeriodGrain] = React.useState<PeriodGrain>('month');
    const [showPeriodChart, setShowPeriodChart] = React.useState(false);
    const periodRows = React.useMemo(() => rollUpPeriods(periodBreakdown, periodGrain), [periodBreakdown, periodGrain]);
    const [data, setData] = React.useState<{ id: string, invoiceNumber: string, status: string, statusId: string, nextStatusId?: string, invoiceDate: Date, provisionDate: Date, isLinkedToTx: boolean, isFullySatisfied: boolean, amount: number, company: string, email: string, emailSentDate: Date | null, lastEmailDate?: Date | null }[]>([]);

    // Lazy loading (infinite scroll) - PAGE_SIZE.current below tracks which
    // page a given `fetch` response belongs to (set synchronously right
    // before each request), so the response-handling effect knows whether to
    // replace `data` (page 1 / a filter-equivalent reset) or append to it
    // (page > 1, i.e. "load more").
    const INVOICES_PAGE_SIZE = 50;
    const [invoicesPage, setInvoicesPage] = React.useState(1);
    const [hasMoreInvoices, setHasMoreInvoices] = React.useState(false);
    const [isLoadingMoreInvoices, setIsLoadingMoreInvoices] = React.useState(false);
    const [totalInvoicesCount, setTotalInvoicesCount] = React.useState<number | null>(null);
    // Sum of `total` across every invoice matching the current filters, not
    // just the currently-loaded page - computed server-side (see totalAmount
    // in invoice-controller.js) so it stays correct regardless of how much
    // of the filtered set has actually been paginated in yet.
    const [totalInvoicesAmount, setTotalInvoicesAmount] = React.useState<number | null>(null);
    const requestedInvoicePageRef = React.useRef(1);
    const DEFAULT_SORT_BY = 'issueDate';
    const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc';
    const [sortBy, setSortBy] = React.useState<string>(DEFAULT_SORT_BY);
    const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>(DEFAULT_SORT_ORDER);
    // Clicking a column's header label/sort arrow now sorts immediately,
    // separately from opening that column's filter popover (which still
    // offers the same "Sort By X" option, for parity). Same
    // click-to-toggle convention as table-virtual.tsx's own generic sort
    // button: switching to a new column defaults to ascending, clicking the
    // already-active column toggles direction.
    const handleColumnSort = (sortByValue: string) => {
        setSortOrder(prevOrder => (sortBy === sortByValue && prevOrder === 'asc') ? 'desc' : 'asc');
        setSortBy(sortByValue);
    };
    const [columnFilters, setColumnFilters] = React.useState<Record<string, any>>({});
    // Checkbox inside the header search box (its end adornment), so it reads as
    // part of the search: also match invoice line item descriptions.
    // Kept out of columnFilters so ticking it doesn't refetch the list on its own;
    // the request reads it from the ref, and it only refetches once a search is typed.
    const [lineItemsChecked, setLineItemsChecked] = React.useState(false);
    const lineItemsRef = React.useRef(false);
    const lineItemToggle = (
        <Tooltip title="Also search the descriptions of each invoice's line items">
            <FormControlLabel
                sx={{ m: 0, whiteSpace: 'nowrap' }}
                control={<Checkbox size="small" checked={lineItemsChecked} onChange={(e) => {
                    const checked = e.target.checked;
                    lineItemsRef.current = checked;
                    setLineItemsChecked(checked);
                    if (columnFilters.headerKeyword) setColumnFilters(prev => ({ ...prev }));
                }} sx={{ p: 0.25, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }} />}
                label={<Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Incl. line items</Typography>}
            />
        </Tooltip>
    );
    // Mobile-only combined "Sort & Filter" bottom sheet - consolidates the
    // per-column filter popups (otherwise unreachable on mobile, since the
    // card view has no column header row to tap) behind one button next to
    // the search bar, same pattern as the Transactions page's FiltersMobile.
    const [combinedFilterOpen, setCombinedFilterOpen] = React.useState(false);
    const [expandedFilterSections, setExpandedFilterSections] = React.useState<Set<string>>(new Set(['status']));
    const invoiceNumberFilterRef = React.useRef<DescriptionFilterHandle>(null);
    const statusFilterRef = React.useRef<StatusFilterHandle>(null);
    const invoiceDateFilterRef = React.useRef<SimpleDateFilterHandle>(null);
    const provisionDateFilterRef = React.useRef<SimpleDateFilterHandle>(null);
    const amountCombinedFilterRef = React.useRef<AmountFilterHandle>(null);
    const companyFilterRef = React.useRef<DescriptionFilterHandle>(null);
    const emailFilterRef = React.useRef<DescriptionFilterHandle>(null);
    const draftColumnFiltersRef = React.useRef<Record<string, any>>({});
    // Show/hide by status - null means "not yet initialized" (statuses
    // haven't loaded so we don't know which id is Paid yet); the fetch
    // effects wait for this before firing so the very first request already
    // excludes Paid instead of fetching everything and re-filtering after.
    const [selectedStatusIds, setSelectedStatusIds] = React.useState<string[] | null>(null);
    const statusDefaultsAppliedRef = React.useRef(false);

    // import-related state
    const [importFileDetails, setImportFileDetails] = React.useState<FileDetails | null>(null);
    const [openNoProviderPopup, setOpenNoProviderPopup] = React.useState(false);

    const [fetch, setFetch] = useApi();
    const [fetchStatuses, setFetchStatuses] = useApi();
    const [createInvoice, setCreateInvoice] = useApi();
    const [updateInvoice, setUpdateInvoice] = useApi();
    const [updateStatusRequest, setUpdateStatusRequest] = useApi();
    const [resendEmailRequest, setResendEmailRequest] = useApi();
    const [deleteInvoice, setDeleteInvoice] = useApi();
    const [createImportEvent, setCreateImportEvent] = useApi();
    const [importId, setImportId] = React.useState<string | null>(null);
    const [fetchInvoiceByNumber, setFetchInvoiceByNumber] = useApi();
    const [recalculateProvisions, setRecalculateProvisions] = useApi();
    const [settingsMenuAnchor, setSettingsMenuAnchor] = React.useState<null | HTMLElement>(null);

    const handleEnterEditDataStep = React.useCallback(() => {
        const { url, method } = buildApiRequestConfig(routes.invoices.createImportEvent);
        setCreateImportEvent({
            method,
            url,
            body: {
                entityId: user?.entityId,
                moduleName: 'invoices',
                fileName: importFileDetails?.file?.name || 'imported_file.csv'
            }
        });
    }, [user?.entityId, importFileDetails?.file?.name, setCreateImportEvent]);

    React.useEffect(() => {
        document.title = "FineAnts | Invoices";
        return () => {
            // Reset modal state when component unmounts
            setOpenModal('');
            setPreviewInvoiceId(null);
            setSidebarOpen(false);
        };
    }, [setSidebarOpen]);

    React.useEffect(() => {
        setOpenModal('');
        setPreviewInvoiceId(null);
    }, [location.pathname]);

    // Shared by both the initial fetch and load-more - keeps the query
    // params in sync across the two so paging in never drops a filter that
    // page 1 applied.
    const buildInvoiceFetchParams = React.useCallback((page: number) => ([
        { param: 'sortBy', value: sortBy },
        { param: 'sortOrder', value: sortOrder },
        { param: 'invoiceNumber', value: columnFilters.invoiceNumberKeyword },
        { param: 'keyword', value: columnFilters.headerKeyword },
        // Line item search is opt-in: it adds a query over every invoice's lines, so it stays off by default.
        { param: 'includeLineItems', value: columnFilters.headerKeyword && lineItemsRef.current ? 'true' : undefined },
        { param: 'statusIds', value: selectedStatusIds && selectedStatusIds.length > 0 ? selectedStatusIds.join(',') : undefined },
        { param: 'clientCompanyName', value: columnFilters.companyKeyword },
        { param: 'clientEmail', value: columnFilters.emailKeyword },
        { param: 'startDate', value: columnFilters.invoiceDateStart },
        { param: 'endDate', value: columnFilters.invoiceDateEnd },
        { param: 'provisionDateFrom', value: columnFilters.provisionDateStart },
        { param: 'provisionDateTo', value: columnFilters.provisionDateEnd },
        { param: 'minAmount', value: columnFilters.amountKeyword?.[0] },
        { param: 'maxAmount', value: columnFilters.amountKeyword?.[1] },
        { param: 'page', value: page },
        { param: 'pageSize', value: INVOICES_PAGE_SIZE },
    ]), [sortBy, sortOrder, columnFilters, selectedStatusIds]);

    React.useEffect(() => {
        // Wait for the default status selection to be computed first (see
        // the fetchStatuses effect above) so the very first request already
        // excludes Paid, instead of fetching everything and re-filtering.
        if (!user?.entityId || selectedStatusIds === null) return;
        requestedInvoicePageRef.current = 1;
        const { url, method } = buildApiRequestConfig(routes.invoices.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            ...buildInvoiceFetchParams(1),
        ]);
        setFetch({ url, method });
    }, [user?.entityId, createInvoice, updateInvoice, deleteInvoice, selectedStatusIds, buildInvoiceFetchParams]);

    const handleLoadMoreInvoices = React.useCallback(() => {
        if (!user?.entityId || isLoadingMoreInvoices || !hasMoreInvoices || selectedStatusIds === null) return;
        const nextPage = invoicesPage + 1;
        requestedInvoicePageRef.current = nextPage;
        setIsLoadingMoreInvoices(true);
        const { url, method } = buildApiRequestConfig(routes.invoices.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            ...buildInvoiceFetchParams(nextPage),
        ]);
        setFetch({ url, method });
    }, [user?.entityId, invoicesPage, isLoadingMoreInvoices, hasMoreInvoices, selectedStatusIds, buildInvoiceFetchParams, setFetch]);

    // Re-fetches page 1 - used after linking/unlinking a transaction from the
    // "Link to a TX" action (TransactionDetail's onLink), since that happens
    // inside a popup this page doesn't otherwise have a useApi result object
    // for to trigger the main fetch effect's own dependency-driven refetch.
    const refetchInvoices = React.useCallback(() => {
        if (!user?.entityId || selectedStatusIds === null) return;
        requestedInvoicePageRef.current = 1;
        const { url, method } = buildApiRequestConfig(routes.invoices.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            ...buildInvoiceFetchParams(1),
        ]);
        setFetch({ url, method });
    }, [user?.entityId, selectedStatusIds, buildInvoiceFetchParams, setFetch]);

    React.useEffect(() => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.invoiceSettings.fetchStatusesByEntity, [{ param: 'entityId', value: user.entityId }]);
        setFetchStatuses({ url, method });
    }, [user?.entityId, setFetchStatuses]);

    React.useEffect(() => {
        const { data, isLoading } = fetchStatuses;
        if (data && !isLoading) {
            const statusOptions = data.map((status: any) => ({
                label: formatInvoiceStatusLabel(status.name),
                value: String(status.id),
                rawName: status.name,
            }));
            setStatuses(statusOptions);

            // Default: every status shown except Paid (pre-existing) and
            // Voided/Deleted (Xero itself only surfaces those under an
            // explicit "include deleted & voided" view, not as a normal
            // status tab) - applied once (a ref guard, not a dependency on
            // selectedStatusIds, so this doesn't clobber the user's own
            // later toggling if statuses re-fetches). Matched against the
            // raw synced name, not the display label, so this still works
            // regardless of label wording.
            if (!statusDefaultsAppliedRef.current) {
                statusDefaultsAppliedRef.current = true;
                setSelectedStatusIds(
                    statusOptions
                        .filter((s: any) => {
                            const raw = (s.rawName || '').trim().toUpperCase();
                            return raw !== 'PAID' && !XERO_HIDDEN_BY_DEFAULT.has(raw);
                        })
                        .map((s: any) => s.value)
                );
            }
        }
    }, [fetchStatuses]);

    React.useEffect(() => {
        const { data, isLoading, error } = createImportEvent;
        if (!isLoading && data) {
            const newImportId = data.importEvent?.id || data.id;
            setImportId(newImportId);
        }
        if (error) {
            showError('Failed to initialize import. Please try again.');
        }
    }, [createImportEvent, showError]);

    // Helper function to get status name from ID
    const getStatusNameById = React.useCallback((statusId: string | number) => {
        const statusObj = fetchStatuses.data?.find((s: any) => String(s.id) === String(statusId));
        return formatInvoiceStatusLabel(statusObj?.name) || String(statusId);
    }, [fetchStatuses.data]);

    React.useEffect(() => {
        const { data, isLoading, error } = fetch;
        if (data && !isLoading && !error && fetchStatuses.data) {
            const mapped = (data?.invoices || []).map((invoice: any) => {
                const statusObj = fetchStatuses.data?.find((s: any) => String(s.id) === String(invoice.status));
                return {
                    id: invoice.id,
                    invoiceNumber: invoice.invoiceNumber,
                    status: formatInvoiceStatusLabel(statusObj?.name) || String(invoice.status),
                    statusId: String(invoice.status),
                    nextStatusId: statusObj?.nextStatusId,
                    invoiceDate: invoice.issueDate,
                    provisionDate: invoice.provisionDate,
                    isLinkedToTx: !!invoice.isLinkedToTx,
                    isFullySatisfied: !!invoice.isFullySatisfied,
                    amount: invoice.total,
                    company: invoice.clientCompanyName,
                    email: invoice.clientEmail,
                    emailSentDate: invoice.emailSentDate ? new Date(invoice.emailSentDate) : null,
                    lastEmailDate: invoice._lifecycle?.lastEmail ? new Date(invoice._lifecycle.lastEmail) : null
                };
            });
            const requestedPage = requestedInvoicePageRef.current;
            setData(prev => requestedPage === 1 ? mapped : [...prev, ...mapped]);
            setInvoicesPage(requestedPage);
            setHasMoreInvoices(!!data?.pagination?.hasMore);
            setTotalInvoicesCount(data?.pagination?.totalItems ?? null);
            setTotalInvoicesAmount(data?.pagination?.totalAmount ?? null);
            if (requestedPage === 1) setPeriodBreakdown(data?.periodBreakdown ?? []);
            if (data?.statusBreakdown) {
                setStatusBreakdown(Object.fromEntries(
                    data.statusBreakdown.map((row: { status: number; count: number; totalAmount: number }) =>
                        [String(row.status), { count: row.count, totalAmount: row.totalAmount }])
                ));
            }
            setIsLoadingMoreInvoices(false);
            setNextInvoiceNumber(data?.nextInvoiceNumber || '00000001');
            setProvisionDateOffset(data?.provisionDateOffset || 0);
            setTermsAndConditions(data?.termsAndConditions || '');
        }
        if (error) {
            showError("Failed to fetch invoices.");
            setIsLoadingMoreInvoices(false);
        }
    }, [fetch, fetchStatuses.data]);

    // Handle opening invoice edit modal from transaction detail via URL params.
    // Looks the invoice up directly by number instead of searching the
    // currently-loaded/paginated `data` array - that array only ever holds
    // whatever page (and column filter) happens to be active, so an invoice
    // outside the first page silently failed to open the modal at all.
    const invoiceNumberParam = searchParams.get('invoiceNumber');
    React.useEffect(() => {
        if (!invoiceNumberParam || !user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.invoices.fetchByEntity, [
            { param: 'entityId', value: user.entityId },
            { param: 'invoiceNumber', value: invoiceNumberParam },
            { param: 'page', value: 1 },
            { param: 'pageSize', value: 5 },
        ]);
        setFetchInvoiceByNumber({ url, method });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceNumberParam, user?.entityId]);

    React.useEffect(() => {
        const { data: lookupData, isLoading, error } = fetchInvoiceByNumber;
        if (isLoading || (!lookupData && !error)) return;
        const invoiceNumber = searchParams.get('invoiceNumber');
        if (!invoiceNumber) return;

        const newParams = new URLSearchParams(searchParams);
        newParams.delete('invoiceNumber');
        setSearchParams(newParams, { replace: true });

        // invoiceNumber is a LIKE match server-side, so filter for the exact match
        const match = (lookupData?.invoices || []).find((inv: any) => inv.invoiceNumber === invoiceNumber);
        if (match) {
            setSelectedInvoiceId(parseInt(match.id));
            setOpenModal('edit');
        } else {
            showError(`Invoice ${invoiceNumber} not found.`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchInvoiceByNumber]);

    React.useEffect(() => {
        const { data, isLoading, error } = createInvoice;
        if (data && !isLoading && !error) {
            showSuccess("Invoice added successfully.");
            setOpenModal('');
        }
        if (error) {
            showError("Failed to add invoice.");
        }
    }, [createInvoice]);

    React.useEffect(() => {
        const { data, isLoading, error } = updateInvoice;
        if (data && !isLoading && !error) {
            showSuccess("Invoice updated successfully.");
            setOpenModal('');
            if (pendingSendEmailOnUpdate && data.id) {
                const { url, method } = buildApiRequestConfig(routes.invoices.resendEmail, [{ param: 'id', value: data.id }]);
                setResendEmailRequest({ url, method });
            }
            setPendingSendEmailOnUpdate(false);
        }
        if (error) {
            showError("Failed to update invoice.");
            setPendingSendEmailOnUpdate(false);
        }
    }, [updateInvoice, pendingSendEmailOnUpdate, setResendEmailRequest]);

    React.useEffect(() => {
        const { data, isLoading, error } = deleteInvoice;
        if (data && !isLoading && !error) {
            showSuccess("Invoice deleted successfully.");
            setOpenModal('');
        }
        if (error) {
            showError("Failed to delete invoice.");
            setOpenModal('');
        }
    }, [deleteInvoice]);

    React.useEffect(() => {
        const { data, isLoading, error } = recalculateProvisions;
        if (data && !isLoading) {
            const touched = data.created + data.updated;
            if (touched > 0) {
                showSuccess(`Recalculated provisions for ${touched} of ${data.total} invoice${data.total === 1 ? '' : 's'} (${data.created} created, ${data.updated} updated)${data.skippedPaid > 0 ? ` - ${data.skippedPaid} already paid, left untouched` : ''}`);
                refetchInvoices();
            } else {
                showError(`No provisions needed recalculating out of ${data.total} invoice${data.total === 1 ? '' : 's'}${data.skippedPaid > 0 ? ` (${data.skippedPaid} already paid)` : ''}`);
            }
        }
        if (error) {
            showError("Failed to recalculate provisions");
        }
    }, [recalculateProvisions]);

    const handleRecalculateProvisions = () => {
        if (!user?.entityId) return;
        const { url, method } = buildApiRequestConfig(routes.invoices.recalculateProvisions, [{ param: 'entityId', value: user.entityId }]);
        setRecalculateProvisions({ url, method, body: {} });
    };

    React.useEffect(() => {
        const { data, isLoading, error } = updateStatusRequest;
        if (data && !isLoading && !error) {
            showSuccess("Invoice status updated successfully.");
            // Refresh the table data
            requestedInvoicePageRef.current = 1;
            const { url, method } = buildApiRequestConfig(routes.invoices.fetchByEntity, [
                { param: 'entityId', value: user?.entityId },
                { param: 'page', value: 1 },
                { param: 'pageSize', value: INVOICES_PAGE_SIZE },
            ]);
            setFetch({ url, method });
        }
        if (error) {
            showError("Failed to update invoice status.");
        }
    }, [updateStatusRequest, user?.entityId, setFetch]);

    const isResendRef = React.useRef(false);

    const handleResendEmail = React.useCallback((row: any) => {
        if (!row.id) {
            showError('Invoice ID not found');
            return;
        }
        if (!row.email) {
            showError('This invoice has no email address to send to');
            return;
        }

        isResendRef.current = !!row.lastEmailDate;
        const { url, method } = buildApiRequestConfig(routes.invoices.resendEmail, [
            { param: 'id', value: row.id }
        ]);
        setResendEmailRequest({ url, method });
    }, [setResendEmailRequest, showError]);

    React.useEffect(() => {
        const { data, isLoading, error } = resendEmailRequest;
        if (data && !isLoading) {
            showSuccess(isResendRef.current ? 'Email resent successfully' : 'Email sent successfully');
        }
        if (error) {
            showError(isResendRef.current ? 'Failed to resend email' : 'Failed to send email');
        }
    }, [resendEmailRequest, showError, showSuccess]);

    const handleSetToNextStatus = React.useCallback((row: any) => {
        if (!row.nextStatusId) {
            showError('No next status available for this invoice');
            return;
        }

        const { url, method } = buildApiRequestConfig(routes.invoices.update, [{ param: 'id', value: row.id }]);
        setUpdateStatusRequest({ 
            url, 
            method,
            body: { status: row.nextStatusId }
        });
    }, [setUpdateStatusRequest, showError]);

    const handleDelete = () => {
        if (selectedInvoiceId) {
            const { url, method } = buildApiRequestConfig(routes.invoices.delete, [{ param: 'id', value: selectedInvoiceId }]);
            setDeleteInvoice({ url, method });
        }
    }

    const handleImportClients = async (selectedRows: any[], selectedRowIds: Set<string>, onProgress?: (state: any) => void) => {
        try {
            const formatConfig = importFileDetails?.provider?.importFormat 
                ? (typeof importFileDetails.provider.importFormat === 'string' 
                  ? JSON.parse(importFileDetails.provider.importFormat) 
                  : importFileDetails.provider.importFormat)
                : null;
            const mappedClients = selectedRows.map((row: any) => {
                const client: Record<string, any> = {
                    entityId: user?.entityId
                };

                if (formatConfig?.clientMapping) {
                    const mapping = formatConfig.clientMapping;
                    Object.entries(mapping).forEach(([fieldName, csvCol]: [string, any]) => {
                        if (csvCol && typeof csvCol === 'string') {
                            const value = row[csvCol];
                            if (value) {
                                client[fieldName] = value;
                            }
                        }
                    });
                }

                return client;
            });

            // Chunk clients (50 per chunk)
            const CHUNK_SIZE = 50;
            const chunks: any[][] = [];
            for (let i = 0; i < mappedClients.length; i += CHUNK_SIZE) {
                chunks.push(mappedClients.slice(i, i + CHUNK_SIZE));
            }

            if (!importId) {
                showError('Import session not initialized. Please try again.');
                if (onProgress) {
                    onProgress({
                        currentChunk: 0,
                        totalChunks: 0,
                        processedRows: 0,
                        totalRows: 0,
                        status: 'error',
                        importedEntries: []
                    });
                }
                return;
            }

            if (onProgress) {
                onProgress({
                    currentChunk: 0,
                    totalChunks: chunks.length,
                    processedRows: 0,
                    totalRows: mappedClients.length,
                    status: 'processing',
                    importedEntries: []
                });
            }

            const allImportedClients: any[] = [];
            for (let i = 0; i < chunks.length; i++) {
                const { url, method } = buildApiRequestConfig(routes.client.import);
                const token = Cookies.get('token');
                let headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }
                
                const response = await axios({
                    method,
                    url,
                    headers,
                    data: { clients: chunks[i], importId }
                });

                if (response.data?.created) {
                    allImportedClients.push(...response.data.created);
                }

                if (onProgress) {
                    onProgress({
                        currentChunk: i + 1,
                        totalChunks: chunks.length,
                        processedRows: allImportedClients.length,
                        totalRows: mappedClients.length,
                        status: 'processing',
                        importedEntries: allImportedClients
                    });
                }

                // 200ms delay between chunks
                if (i < chunks.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
            }

            if (onProgress) {
                onProgress({
                    currentChunk: chunks.length,
                    totalChunks: chunks.length,
                    processedRows: allImportedClients.length,
                    totalRows: mappedClients.length,
                    status: 'success',
                    importedEntries: allImportedClients
                });
            }
        } catch (err: any) {
            console.error('Client import error:', err);
            showError(err?.response?.data?.message || err?.toString?.() || 'Failed to import clients');
            if (onProgress) {
                onProgress({
                    currentChunk: 0,
                    totalChunks: 0,
                    processedRows: 0,
                    totalRows: 0,
                    status: 'error',
                    importedEntries: []
                });
            }
        }
    };

    const handleImportInvoices = async (selectedRows: any[], selectedRowIds: Set<string>, onProgress?: (state: any) => void) => {
        try {
            const formatConfig = importFileDetails?.provider?.importFormat 
                ? (typeof importFileDetails.provider.importFormat === 'string' 
                  ? JSON.parse(importFileDetails.provider.importFormat) 
                  : importFileDetails.provider.importFormat)
                : null;

            if (!formatConfig?.invoiceMapping) {
                showError('Invoice mapping not configured');
                if (onProgress) {
                    onProgress({
                        currentChunk: 0,
                        totalChunks: 0,
                        processedRows: 0,
                        totalRows: 0,
                        status: 'error',
                        importedEntries: []
                    });
                }
                return;
            }

            // Group rows by invoice number
            const invoiceNumberColumn = formatConfig.invoiceMapping.extInvoiceNumber;
            const groupedByInvoice: Record<string, any[]> = {};
            selectedRows.forEach((row: any) => {
                const invoiceNumber = row[invoiceNumberColumn] || 'Unknown';
                if (!groupedByInvoice[invoiceNumber]) {
                    groupedByInvoice[invoiceNumber] = [];
                }
                groupedByInvoice[invoiceNumber].push(row);
            });

            // Map grouped rows to invoice structure
            const mappedInvoices = Object.entries(groupedByInvoice).map(([invoiceNumber, lineItemRows]) => {
                const invoiceMapping = formatConfig.invoiceMapping;
                const lineItemMapping = invoiceMapping.lineItemMapping || {};

                // Get invoice header data from first row
                const headerRow = lineItemRows[0];
                const invoice: Record<string, any> = {
                    entityId: user?.entityId,
                    invoiceNumber: headerRow[invoiceMapping.extInvoiceNumber] || invoiceNumber,
                    extInvoiceNumber: headerRow[invoiceMapping.extInvoiceNumber] || invoiceNumber
                };

                // Field name mapping from CSV config to API response
                const fieldMapping: Record<string, string> = {
                    date: 'issueDate',
                    amount: 'total',
                    taxAmount: 'taxAmount',
                    dueDate: 'dueDate',
                    currency: 'currency',
                    company: 'clientCompanyName',
                    recepient: 'clientName',
                    email: 'clientEmail',
                    phone: 'clientPhone',
                    fax: 'clientFax',
                    address1: 'clientAddress1',
                    address2: 'clientAddress2',
                    suburb: 'clientSuburb',
                    state: 'clientState',
                    postalCode: 'clientPostalCode',
                    country: 'clientCountry',
                    descriptor: 'description'
                };

                // Map invoice header fields
                Object.entries(invoiceMapping).forEach(([fieldName, csvCol]: [string, any]) => {
                    if (fieldName !== 'lineItemMapping' && fieldName !== 'extInvoiceNumber' && csvCol && typeof csvCol === 'string' && headerRow[csvCol]) {
                        const apiFieldName = fieldMapping[fieldName] || fieldName;
                        invoice[apiFieldName] = headerRow[csvCol];
                    }
                });

                // Ensure subtotal and total are set (use amount or provided values)
                if (invoiceMapping.amount && headerRow[invoiceMapping.amount]) {
                    invoice.subtotal = invoice.total || headerRow[invoiceMapping.amount];
                }

                // Map line items with tax handling
                invoice.invoiceItems = lineItemRows.map((row: any) => {
                    const lineItem: Record<string, any> = {};
                    
                    // Map standard line item fields
                    Object.entries(lineItemMapping).forEach(([fieldName, csvCol]: [string, any]) => {
                        // Skip tax-specific fields and lineTotal (compute it manually) - they're handled separately
                        if (!['taxName', 'taxAmount', 'taxTotal', 'lineTotal'].includes(fieldName) && csvCol && typeof csvCol === 'string' && row[csvCol]) {
                            lineItem[fieldName] = row[csvCol];
                        }
                    });
                    
                    // Compute lineTotal manually: quantity * unitPrice
                    const quantityColumn = lineItemMapping.quantity;
                    const unitPriceColumn = lineItemMapping.unitPrice;
                    if (quantityColumn && unitPriceColumn && row[quantityColumn] && row[unitPriceColumn]) {
                        const quantity = parseFloat(row[quantityColumn]);
                        const unitPrice = parseFloat(row[unitPriceColumn]);
                        if (!isNaN(quantity) && !isNaN(unitPrice)) {
                            lineItem.lineTotal = quantity * unitPrice;
                        }
                    }
                    
                    // Handle taxes: if taxName is provided in the mapping and data, apply it
                    const taxNameColumn = lineItemMapping.taxName;
                    if (taxNameColumn && row[taxNameColumn]) {
                        // Store tax information on the line item
                        // This will be matched to system taxes by name during processing
                        lineItem.taxName = row[taxNameColumn];
                        if (lineItemMapping.taxAmount && row[lineItemMapping.taxAmount]) {
                            lineItem.taxAmount = row[lineItemMapping.taxAmount];
                        }
                        if (lineItemMapping.taxTotal && row[lineItemMapping.taxTotal]) {
                            lineItem.taxTotal = row[lineItemMapping.taxTotal];
                        }
                    }
                    
                    return lineItem;
                });

                // Process taxes: group by tax name and create appliedTaxes array per line item
                const taxNamesByLineItem = new Map<number, string[]>();
                lineItemRows.forEach((row: any, rowIndex: number) => {
                    const taxNameColumn = lineItemMapping.taxName;
                    if (taxNameColumn && row[taxNameColumn]) {
                        const taxName = row[taxNameColumn];
                        if (!taxNamesByLineItem.has(rowIndex)) {
                            taxNamesByLineItem.set(rowIndex, []);
                        }
                        if (!taxNamesByLineItem.get(rowIndex)!.includes(taxName)) {
                            taxNamesByLineItem.get(rowIndex)!.push(taxName);
                        }
                    }
                });
                
                // Add appliedTaxes as metadata for later processing
                if (taxNamesByLineItem.size > 0) {
                    invoice.lineItemTaxMapping = Object.fromEntries(taxNamesByLineItem);
                }

                return invoice;
            });

            // Chunk invoices (20 per chunk)
            const CHUNK_SIZE = 20;
            const chunks: any[][] = [];
            for (let i = 0; i < mappedInvoices.length; i += CHUNK_SIZE) {
                chunks.push(mappedInvoices.slice(i, i + CHUNK_SIZE));
            }

            // Calculate distinct clients from selectedRows
            const clientMapping = formatConfig.invoiceMapping;
            const distinctClients = new Set<string>();
            selectedRows.forEach((row: any) => {
                const clientSignature = [
                    clientMapping.company ? row[clientMapping.company] : '',
                    clientMapping.recepient ? row[clientMapping.recepient] : '',
                ].join('|');
                if (clientSignature) {
                    distinctClients.add(clientSignature);
                }
            });
            const clientsFound = distinctClients.size;

            if (!importId) {
                showError('Import session not initialized. Please try again.');
                if (onProgress) {
                    onProgress({
                        currentChunk: 0,
                        totalChunks: 0,
                        processedRows: 0,
                        totalRows: 0,
                        status: 'error',
                        importedEntries: []
                    });
                }
                return;
            }

            if (onProgress) {
                onProgress({
                    currentChunk: 0,
                    totalChunks: chunks.length,
                    processedRows: 0,
                    totalRows: mappedInvoices.length,
                    status: 'processing',
                    importedEntries: []
                });
            }

            let totalImportedInvoices = 0;
            for (let i = 0; i < chunks.length; i++) {
                const { url, method } = buildApiRequestConfig(routes.invoices.import);

                const token = Cookies.get('token');
                let headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                const response = await axios({
                    method,
                    url,
                    headers,
                    data: { invoices: chunks[i], importId }
                });

                if (response.data?.createdCount !== undefined) {
                    totalImportedInvoices += response.data.createdCount;
                }

                if (onProgress) {
                    onProgress({
                        currentChunk: i + 1,
                        totalChunks: chunks.length,
                        processedRows: totalImportedInvoices,
                        totalRows: mappedInvoices.length,
                        status: 'processing',
                        importedEntries: []
                    });
                }

                // 200ms delay between chunks
                if (i < chunks.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                }
            }

            if (onProgress) {
                onProgress({
                    currentChunk: chunks.length,
                    totalChunks: chunks.length,
                    processedRows: totalImportedInvoices,
                    totalRows: mappedInvoices.length,
                    status: 'success',
                    importedEntries: [],
                    clientsFound: clientsFound,
                    invoicesFound: mappedInvoices.length,
                    invoicesImported: totalImportedInvoices
                });
            }
        } catch (err: any) {
            console.error('Invoice import error:', err);
            showError(err?.response?.data?.message || err?.toString?.() || 'Failed to import invoices');
            if (onProgress) {
                onProgress({
                    currentChunk: 0,
                    totalChunks: 0,
                    processedRows: 0,
                    totalRows: 0,
                    status: 'error',
                    importedEntries: []
                });
            }
        }
    };

    const handleCreate = (invoiceData: InvoiceData) => {
        const taxBreakdownData = (invoiceData.taxes || []).map((tax: any) => ({
            id: tax.id,
            name: tax.name,
            rate: tax.rate,
            amount: tax.amount,
        }));

        const { url, method } = buildApiRequestConfig(routes.invoices.create);
        setCreateInvoice({ 
            url, 
            method, 
            body: {  
                entityId: user?.entityId,
                clientId: invoiceData.clientId,
                invoiceNumber: invoiceData.invoiceNumber,
                issueDate: invoiceData.issuedDate,
                clientName: invoiceData.recipientName,
                clientCompanyName: invoiceData.companyName,
                clientAddress1: invoiceData.companyAddress,
                clientAddress2: invoiceData.companyAddress2,
                clientPostalCode: invoiceData.companyPostalCode,
                clientSuburb: invoiceData.companySuburb,
                clientState: invoiceData.companyState,
                clientPhone: invoiceData.companyPhone,
                clientFax: invoiceData.companyFax,
                clientEmail: invoiceData.companyEmail,
                invoiceDate: invoiceData.issuedDate,
                subtotal: invoiceData.subTotal,
                total: invoiceData.grandTotal,
                description: invoiceData.invoiceDescriptor || null,
                termsAndConditions: invoiceData.termsAndConditions,
                status: invoiceData.status,
                taxBreakdown: JSON.stringify(taxBreakdownData),
                sendEmail: invoiceData.sendEmail !== false,
                provisionDateOffset: invoiceData.provisionDateOffset,
                confidenceLevel: invoiceData.confidenceLevel,
                invoiceItems: invoiceData.items?.map((item: any) => ({
                    type: item.type || 'item',
                    description: item.description,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    appliedTaxesIds: item.appliedTaxes?.join(',') || '',
                    commentHtml: item.commentHtml || null,
                }))
            }
        });
    }

    const handleUpdate = (invoiceData: InvoiceData) => {
        const taxBreakdownData = (invoiceData.taxes || []).map((tax: any) => ({
            id: tax.id,
            name: tax.name,
            rate: tax.rate,
            amount: tax.amount,
        }));

        setPendingSendEmailOnUpdate(!!invoiceData.sendEmail);

        const { url, method } = buildApiRequestConfig(routes.invoices.update, [{ param: 'id', value: selectedInvoiceId }]);
        setUpdateInvoice({
            url,
            method,
            body: {  
                clientId: invoiceData.clientId,
                issueDate: invoiceData.issuedDate,
                clientName: invoiceData.recipientName,
                clientCompanyName: invoiceData.companyName,
                clientAddress1: invoiceData.companyAddress,
                clientAddress2: invoiceData.companyAddress2,
                clientPostalCode: invoiceData.companyPostalCode,
                clientSuburb: invoiceData.companySuburb,
                clientState: invoiceData.companyState,
                clientPhone: invoiceData.companyPhone,
                clientFax: invoiceData.companyFax,
                clientEmail: invoiceData.companyEmail,
                invoiceDate: invoiceData.issuedDate,
                subtotal: invoiceData.subTotal,
                total: invoiceData.grandTotal,
                description: invoiceData.invoiceDescriptor || null,
                termsAndConditions: invoiceData.termsAndConditions,
                status: invoiceData.status,
                taxBreakdown: JSON.stringify(taxBreakdownData),
                provisionDateOffset: invoiceData.provisionDateOffset,
                confidenceLevel: invoiceData.confidenceLevel,
                invoiceItems: invoiceData.items?.map((item: any) => ({
                    type: item.type || 'item',
                    description: item.description,
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    appliedTaxesIds: item.appliedTaxes?.join(',') || '',
                    commentHtml: item.commentHtml || null,
                }))
            }
        });
    }

    // AmountFilter/date-range defaults need overall min/max bounds - there's
    // no server-computed aggregate for invoices (unlike the transactions
    // page's companyStatus.lowestAmount/highestAmount), so this is a
    // best-effort estimate from whatever's currently loaded. Once a filter
    // narrows `data`, naively recomputing from `data` would shrink these
    // bounds along with it (the slider's max permanently dropping once you
    // filter to a smaller range) - so these only ever widen, never shrink,
    // ratcheting outward as more of the unfiltered data is seen.
    const [amountBounds, setAmountBounds] = React.useState({ min: 0, max: 100000 });
    const [invoiceDateBoundsRaw, setInvoiceDateBoundsRaw] = React.useState<{ min: Date; max: Date } | null>(null);
    const [provisionDateBoundsRaw, setProvisionDateBoundsRaw] = React.useState<{ min: Date; max: Date } | null>(null);
    const invoiceDateBounds = invoiceDateBoundsRaw || { min: todayPlusYears(-1), max: todayPlusYears(1) };
    const provisionDateBounds = provisionDateBoundsRaw || { min: todayPlusYears(-1), max: todayPlusYears(1) };

    React.useEffect(() => {
        if (data.length === 0) return;
        const amounts = data.map(d => Number(d.amount) || 0);
        const newMin = Math.min(0, ...amounts);
        const newMax = Math.max(...amounts, 100);
        setAmountBounds(prev => ({
            min: Math.min(prev.min, newMin),
            max: Math.max(prev.max, newMax),
        }));

        const invoiceDates = data.map(d => new Date(d.invoiceDate)).filter(d => !isNaN(d.getTime()));
        if (invoiceDates.length) {
            const newMinDate = new Date(Math.min(...invoiceDates.map(d => d.getTime())));
            const newMaxDate = new Date(Math.max(...invoiceDates.map(d => d.getTime())));
            setInvoiceDateBoundsRaw(prev => !prev ? { min: newMinDate, max: newMaxDate } : {
                min: newMinDate < prev.min ? newMinDate : prev.min,
                max: newMaxDate > prev.max ? newMaxDate : prev.max,
            });
        }

        const provisionDates = data.map(d => d.provisionDate ? new Date(d.provisionDate) : null).filter((d): d is Date => !!d && !isNaN(d.getTime()));
        if (provisionDates.length) {
            const newMinDate = new Date(Math.min(...provisionDates.map(d => d.getTime())));
            const newMaxDate = new Date(Math.max(...provisionDates.map(d => d.getTime())));
            setProvisionDateBoundsRaw(prev => !prev ? { min: newMinDate, max: newMaxDate } : {
                min: newMinDate < prev.min ? newMinDate : prev.min,
                max: newMaxDate > prev.max ? newMaxDate : prev.max,
            });
        }
    }, [data]);

    // Reverse of the server field names used in sortBy - several columns'
    // ids diverge from the actual DB field (amount->total, company/email
    // use the client* field names) so the header arrow needs this mapping
    // to highlight the right column.
    const sortColumnIdFor = (serverSortBy: string): string => {
        switch (serverSortBy) {
            case 'total': return 'amount';
            case 'clientCompanyName': return 'company';
            case 'clientEmail': return 'email';
            case 'issueDate': return 'invoiceDate';
            default: return serverSortBy;
        }
    };

    // Combined mobile filter sheet - each embedded section's onApply writes
    // into this ref instead of straight into columnFilters (multiple
    // synchronous calls in one click would each close over the same stale
    // columnFilters and clobber each other); the single Apply button below
    // commits the fully-merged result once at the end. Same pattern as the
    // Transactions page's FiltersMobile combined modal.
    const handleCombinedFilterDraftApply = (params: Record<string, any>) => {
        draftColumnFiltersRef.current = { ...draftColumnFiltersRef.current, ...params };
    };

    const handleApplyAllCombinedFilters = () => {
        draftColumnFiltersRef.current = { ...columnFilters };
        invoiceNumberFilterRef.current?.applyNow();
        statusFilterRef.current?.applyNow();
        invoiceDateFilterRef.current?.applyNow();
        provisionDateFilterRef.current?.applyNow();
        amountCombinedFilterRef.current?.applyNow();
        companyFilterRef.current?.applyNow();
        emailFilterRef.current?.applyNow();
        setColumnFilters((prev: Record<string, any>) => {
            const { sortBy: draftSortBy, sortDirection: draftSortDirection, ...rest } = draftColumnFiltersRef.current as any;
            if (draftSortBy !== undefined) {
                setSortBy(draftSortBy === null ? DEFAULT_SORT_BY : draftSortBy);
                setSortOrder(draftSortDirection === 'ASC' ? 'asc' : 'desc');
            }
            return { ...prev, ...rest };
        });
        setCombinedFilterOpen(false);
    };

    const handleClearAllCombinedFilters = () => {
        draftColumnFiltersRef.current = {};
        invoiceNumberFilterRef.current?.clear();
        statusFilterRef.current?.clear();
        invoiceDateFilterRef.current?.clear();
        provisionDateFilterRef.current?.clear();
        amountCombinedFilterRef.current?.clear();
        companyFilterRef.current?.clear();
        emailFilterRef.current?.clear();
        setColumnFilters({});
        setSelectedStatusIds(statuses.map(s => String(s.value)));
    };

    const toggleFilterSection = (section: string) => {
        setExpandedFilterSections(prev => {
            const next = new Set(prev);
            if (next.has(section)) next.delete(section); else next.add(section);
            return next;
        });
    };

    const CombinedFilterSection: React.FC<{ title: string; section: string; children: React.ReactNode }> = ({ title, section, children }) => (
        <Box sx={{ mb: 1, border: '1px solid #e0ecf7', borderRadius: 1.5, overflow: 'hidden' }}>
            <Box
                onClick={() => toggleFilterSection(section)}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    px: 1.5,
                    background: '#f5f9fe',
                    cursor: 'pointer',
                    '&:hover': { background: '#e8f4f0' },
                }}
            >
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--color-default-text)' }}>{title}</Typography>
                {expandedFilterSections.has(section) ? <ChevronUp size={18} color="var(--color-primary)" /> : <ChevronDown size={18} color="var(--color-primary)" />}
            </Box>
            <Collapse in={expandedFilterSections.has(section)}>
                <Box sx={{ p: 1.5, background: 'var(--color-page-background)' }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );

    // Client-side only (filters whatever page(s) of invoices are already
    // loaded, not a server query) - same tradeoff as the Transactions page's
    // Action column filter: isLinkedToTx/isFullySatisfied depend on a
    // reverse-link scan across all of the entity's transactions, which is
    // simple to do once per fetched page server-side but awkward to turn
    // into a SQL WHERE clause ahead of pagination's LIMIT/OFFSET.
    const displayedData = React.useMemo(() => {
        const filter: { linked: string; fullySatisfied: string } = columnFilters.linkStatus || { linked: 'none', fullySatisfied: 'none' };
        if (filter.linked === 'none' && filter.fullySatisfied === 'none') return data;
        return data.filter(row => {
            if (filter.linked === 'include' && !row.isLinkedToTx) return false;
            if (filter.linked === 'exclude' && row.isLinkedToTx) return false;
            if (filter.fullySatisfied === 'include' && !row.isFullySatisfied) return false;
            if (filter.fullySatisfied === 'exclude' && row.isFullySatisfied) return false;
            return true;
        });
    }, [data, columnFilters.linkStatus]);

    const columns = [
        {
            id: 'invoiceNumber',
            label: 'ID',
            width: 100,
            type: 'custom' as const,
            onSort: () => handleColumnSort('invoiceNumber'),
            customElement: (val: any, row: any) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main, textDecoration: 'underline', cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setPreviewInvoiceId(row.id); }}>{val}</Typography>
                </Box>
            ),
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: any) => void) => (
                <DescriptionFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    onApply={(params: any) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            invoiceNumberKeyword: params.descriptionKeyword
                        }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy(DEFAULT_SORT_BY);
                            setSortOrder(DEFAULT_SORT_ORDER);
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentDescriptionKeyword={columnFilters.invoiceNumberKeyword}
                    fieldLabel="ID"
                    sortByValue="invoiceNumber"
                    placeholder="search ID"
                />
            ),
            isFilterActive: () => !!columnFilters.invoiceNumberKeyword
        },
        {
            id: 'status',
            label: 'Status',
            width: 80,
            type: 'custom' as const,
            onSort: () => handleColumnSort('status'),
            mobilePrimary: true,
            mobileOrder: 2,
            mobileWidth: 75,
            mobileHideLabel: true,
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: any) => void) => (
                <StatusFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    options={statuses.map(s => {
                        const breakdown = statusBreakdown[String(s.value)];
                        return {
                            id: String(s.value),
                            label: s.label,
                            count: breakdown?.count ?? 0,
                            totalAmount: formatAmount ? formatAmount(breakdown?.totalAmount ?? 0) : String(breakdown?.totalAmount ?? 0),
                        };
                    })}
                    selectedIds={selectedStatusIds || []}
                    sortByValue="status"
                    sortFieldLabel="Status"
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    onApply={(result) => {
                        setSelectedStatusIds(result.selectedIds);
                        if (result.sortBy) {
                            setSortBy(result.sortBy);
                            setSortOrder(result.sortDirection === 'ASC' ? 'asc' : 'desc');
                        }
                        onClose();
                    }}
                />
            ),
            isFilterActive: () => !!selectedStatusIds && !!statuses.length && selectedStatusIds.length < statuses.length,
            customElement: (val: any, row: any) => {
                const tooltipSx = {
                    maxWidth: 400,
                    fontSize: 13,
                    p: 2,
                    color: 'black',
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                };
                // val is already the mapped Xero-style label (see formatInvoiceStatusLabel
                // in the row-building effect above) - no further transform needed.
                const statusText = <Typography sx={{ fontWeight: 600 }}>{val}</Typography>;

                // Never sent - no envelope icon to hang the tooltip off of, so
                // the tooltip goes on the status text itself instead.
                if (!row.lastEmailDate) {
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip
                                title={<Typography fontSize={13} color="black">This invoice hasn't been sent yet.</Typography>}
                                componentsProps={{ tooltip: { sx: tooltipSx } }}
                            >
                                {statusText}
                            </Tooltip>
                        </Box>
                    );
                }

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {statusText}
                        <Tooltip
                            title={<Typography fontSize={13} color="black">{`Invoice: E – ${formatDate(row.lastEmailDate)}`}</Typography>}
                            componentsProps={{ tooltip: { sx: tooltipSx } }}
                        >
                            <MailCheck size={14} color={theme.palette.primary.main} style={{ cursor: 'pointer' }} />
                        </Tooltip>
                    </Box>
                );
            },
        },
        {
            id: 'invoiceDate',
            label: 'Invoice Date',
            type: 'date' as const,
            width: 140,
            inlineEdit: false,
            onSort: () => handleColumnSort('issueDate'),
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: any) => void) => (
                <SimpleDateFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    onApply={(params) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            invoiceDateStart: params.startDate,
                            invoiceDateEnd: params.endDate,
                        }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy(DEFAULT_SORT_BY);
                            setSortOrder(DEFAULT_SORT_ORDER);
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentStartDate={columnFilters.invoiceDateStart}
                    currentEndDate={columnFilters.invoiceDateEnd}
                    fieldLabel="Invoice Date"
                    sortByValue="issueDate"
                    defaultRangeStart={invoiceDateBounds.min}
                    defaultRangeEnd={invoiceDateBounds.max}
                />
            ),
            isFilterActive: () => !!(columnFilters.invoiceDateStart || columnFilters.invoiceDateEnd),
        },
        {
            id: 'provisionDate',
            label: 'Provision Date',
            type: 'custom' as const,
            width: 140,
            onSort: () => handleColumnSort('provisionDate'),
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: any) => void) => (
                <SimpleDateFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    onApply={(params) => {
                        setColumnFilters(prev => ({
                            ...prev,
                            provisionDateStart: params.startDate,
                            provisionDateEnd: params.endDate,
                        }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy(DEFAULT_SORT_BY);
                            setSortOrder(DEFAULT_SORT_ORDER);
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentStartDate={columnFilters.provisionDateStart}
                    currentEndDate={columnFilters.provisionDateEnd}
                    fieldLabel="Provision Date"
                    sortByValue="provisionDate"
                    defaultRangeStart={provisionDateBounds.min}
                    defaultRangeEnd={provisionDateBounds.max}
                />
            ),
            isFilterActive: () => !!(columnFilters.provisionDateStart || columnFilters.provisionDateEnd),
            customElement: (val: any, row: any) => (
                val ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={() => navigate(`/transaction?invoiceNumber=${row.invoiceNumber}`)}>
                        <Typography sx={{ fontWeight: 600, color: theme.palette.primary.main, textDecoration: 'underline' }}>
                            {formatDate(new Date(val))}
                            <ExternalLink size={14} color={theme.palette.primary.main} style={{ marginLeft: 4 }} />
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ color: 'var(--color-text-muted)' }}>—</Typography>
                    </Box>
                )
            ),
        },
        {
            id: 'amount',
            label: 'Amount',
            type: 'currency' as const,
            width: 100,
            onSort: () => handleColumnSort('amount'),
            mobilePrimary: true,
            mobileOrder: 3,
            // Room for a 7-figure total ("$ 3,981,633.00") - 100 clipped it.
            mobileWidth: 120,
            mobileAlign: 'right' as const,
            mobileHideLabel: true,
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: any) => void) => (
                <AmountFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    min={amountBounds.min}
                    max={amountBounds.max}
                    onApply={(params: any) => {
                        setColumnFilters(prev => ({ ...prev, amountKeyword: params.amountKeyword }));
                        // AmountFilter's sortBy 'amount' - the backend
                        // aliases 'amount'->'total' for this reason.
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy(DEFAULT_SORT_BY);
                            setSortOrder(DEFAULT_SORT_ORDER);
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy: sortBy === 'total' ? 'amount' : sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentAmountKeyword={columnFilters.amountKeyword}
                />
            ),
            isFilterActive: () => !!columnFilters.amountKeyword,
        },
        {
            id: 'company',
            label: 'Company Name',
            width: 150,
            type: 'string' as const,
            onSort: () => handleColumnSort('clientCompanyName'),
            mobilePrimary: true,
            mobileOrder: 1,
            mobileWidth: 110,
            mobileHideLabel: true,
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: any) => void) => (
                <DescriptionFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    onApply={(params: any) => {
                        setColumnFilters(prev => ({ ...prev, companyKeyword: params.descriptionKeyword }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy(DEFAULT_SORT_BY);
                            setSortOrder(DEFAULT_SORT_ORDER);
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentDescriptionKeyword={columnFilters.companyKeyword}
                    fieldLabel="Company Name"
                    sortByValue="clientCompanyName"
                    placeholder="search company name"
                />
            ),
            isFilterActive: () => !!columnFilters.companyKeyword,
        },
        {
            id: 'email',
            label: 'Email',
            width: 200,
            type: 'string' as const,
            showTooltip: true,
            onSort: () => handleColumnSort('clientEmail'),
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void, onApply: (filters: any) => void) => (
                <DescriptionFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    onApply={(params: any) => {
                        setColumnFilters(prev => ({ ...prev, emailKeyword: params.descriptionKeyword }));
                        if (params.sortBy) {
                            setSortBy(params.sortBy);
                            setSortOrder(params.sortDirection === 'ASC' ? 'asc' : 'desc');
                        } else if (params.sortBy === null) {
                            setSortBy(DEFAULT_SORT_BY);
                            setSortOrder(DEFAULT_SORT_ORDER);
                        }
                        onClose();
                    }}
                    currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                    currentDescriptionKeyword={columnFilters.emailKeyword}
                    fieldLabel="Email"
                    sortByValue="clientEmail"
                    placeholder="search email"
                />
            ),
            isFilterActive: () => !!columnFilters.emailKeyword,
        },
        { 
            id: 'actions',
            label: 'Actions',
            size: 60,
            type: 'actions' as const,
            onDelete: (row: any) => {
                setOpenModal('delete');
                setSelectedInvoiceId(row.id);
            },
            onEdit: (row: any) => {
                setSelectedInvoiceId(row.id);
                setOpenModal('edit');
            },
            // Linked-to-a-TX status, shown inline the same way the
            // Transactions page's own Action column shows its Linked/Moved
            // icons - not a separate column.
            actionsPrefix: (row: any) => {
                const label = row.isFullySatisfied ? 'Fully linked - invoice is fully satisfied' : row.isLinkedToTx ? 'Partially linked - some of this invoice is settled' : 'Not linked to any transaction yet';
                const color = row.isFullySatisfied ? 'var(--color-primary)' : row.isLinkedToTx ? '#F59E0B' : '#c7c7c7ff';
                return (
                    <Tooltip title={label} arrow>
                        <Box sx={{ display: 'flex', cursor: 'pointer' }} onClick={() => setLinkInvoiceNumber(row.invoiceNumber)}>
                            <Link size={16} color={color} strokeWidth={row.isLinkedToTx ? 2.5 : 2} />
                        </Box>
                    </Tooltip>
                );
            },
            filterable: true,
            filterComponent: (open: boolean, anchorEl: HTMLElement | null, onClose: () => void) => (
                <InvoiceLinkFilter
                    open={open}
                    anchorEl={anchorEl}
                    onClose={onClose}
                    value={columnFilters.linkStatus || { linked: 'none', fullySatisfied: 'none' }}
                    onChange={(next) => setColumnFilters(prev => ({ ...prev, linkStatus: next }))}
                />
            ),
            isFilterActive: () => {
                const v = columnFilters.linkStatus;
                return !!v && (v.linked !== 'none' || v.fullySatisfied !== 'none');
            },
        },
    ];

    const additionalActions = React.useMemo<AdditionalAction[]>(() => [
        {
            label: (row: any) => `Change Status to ${formatInvoiceStatusLabel(fetchStatuses.data?.find((s: any) => String(s.id) === String(row.nextStatusId))?.name) || 'Next Status'}`,
            icon: 'fastForward' as const,
            onClick: handleSetToNextStatus,
            visible: (row: any): boolean => !!row.nextStatusId,
            disabled: !canUpdate,
        },
        {
            label: (row: any): string => row.lastEmailDate ? 'Resend Email' : 'Send Email',
            icon: 'send' as const,
            onClick: handleResendEmail,
            visible: (_row: any): boolean => canSendEmail,
            disabled: (row: any): boolean => !canUpdate || !row.email,
            tooltip: (row: any) => !canUpdate ? undefined : (!row.email ? 'Add an email address to this invoice first' : undefined),
        },
        {
            label: 'Share / Permissions',
            icon: 'share' as const,
            // Sharing is part of the Documents add-on - without it, the plan prompt.
            onClick: (row: any) => (hasDocumentsAddon
                ? setShareInvoice({ id: row.id, invoiceNumber: row.invoiceNumber })
                : openPlanPrompt(user?.entityId, 'documents')),
            disabled: !canUpdate,
        },
        {
            label: 'View Invoice',
            icon: 'info' as const,
            onClick: (row: any) => setPreviewInvoiceId(row.id),
            visible: (_row: any): boolean => true
        },
        {
            label: 'Link to a Transaction',
            icon: 'link' as const,
            onClick: (row: any) => { setLinkInvoiceNumber(row.invoiceNumber); setAutoOpenLinkModal(true); },
            visible: (_row: any): boolean => true,
            disabled: (row: any): boolean => !canUpdate || row.isFullySatisfied,
            tooltip: (row: any) => !canUpdate ? undefined : (row.isFullySatisfied ? 'This invoice is already fully linked' : undefined),
        }
    ], [fetchStatuses.data, handleSetToNextStatus, handleResendEmail, canUpdate, canSendEmail, hasDocumentsAddon]);

    return (
        <div style={{ width: '100%' }}>
            {openModal === 'import' && (
                <ImportInvoicePopup
                    open={openModal === 'import'}
                    onCancel={() => { 
                        setOpenModal(''); 
                        setImportFileDetails(null);
                        setImportId(null);
                    }}
                    fileDetails={importFileDetails}
                    onFileChange={setImportFileDetails}
                    onProviderChange={() => { 
                        setImportFileDetails(null);
                    }}
                    onEnterEditDataStep={handleEnterEditDataStep}
                    onImport={handleImportClients}
                    onImportInvoices={handleImportInvoices}
                    importId={importId}
                    loading={false}
                />
            )}
            {openNoProviderPopup && (
                <DeleteModal
                    open={openNoProviderPopup}
                    message="No Invoice Provider"
                    description="There are no invoice providers configured. Please contact your administrator."
                    onCancel={() => setOpenNoProviderPopup(false)}
                    onConfirm={() => setOpenNoProviderPopup(false)}
                />
            )}
            {(openModal === 'add' || openModal === 'edit') && (
                <AddInvoicePopup
                    open={openModal === 'add' || openModal === 'edit'}
                    onClose={() => setOpenModal('')}
                    nextInvoiceNumber={nextInvoiceNumber}
                    provisionDateOffset={provisionDateOffset}
                    termsAndConditions={termsAndConditions}
                    isEdit={{ state: openModal === 'edit', id: String(selectedInvoiceId) }}
                    onCreate={openModal === 'add' ? handleCreate : handleUpdate}
                />
            )}
            {openModal === 'delete' && (
                <DeleteModal
                    open={openModal === 'delete'}
                    message="Delete Invoice?"
                    description="This action will permanently remove the selected invoice."
                    image={{ src: staffImage, width: 150, height: 150 }}
                    onCancel={() => setOpenModal('')}
                    onConfirm={handleDelete}
                />
            )}
            {previewInvoiceId && (
                <InvoicePreview
                    open={!!previewInvoiceId}
                    invoiceId={previewInvoiceId}
                    onClose={() => setPreviewInvoiceId(null)}
                />
            )}
            {shareInvoice && (
                <InvoiceShareLink
                    open={!!shareInvoice}
                    invoiceId={shareInvoice.id}
                    invoiceNumber={shareInvoice.invoiceNumber}
                    entityId={user?.entityId ?? null}
                    onClose={() => setShareInvoice(null)}
                />
            )}
            {linkInvoiceNumber && (
                <TransactionDetail
                    open={!!linkInvoiceNumber}
                    transactionId={null}
                    invoiceNumber={linkInvoiceNumber}
                    autoOpenLinkModal={autoOpenLinkModal}
                    onAutoOpenLinkModalClose={() => setAutoOpenLinkModal(false)}
                    onClose={() => { setLinkInvoiceNumber(null); setAutoOpenLinkModal(false); }}
                    onLink={refetchInvoices}
                    // An invoice with no provision transaction (nothing to
                    // show here) opens its edit form instead.
                    onNotFound={() => {
                        const invoice = data.find(inv => inv.invoiceNumber === linkInvoiceNumber);
                        setLinkInvoiceNumber(null);
                        setAutoOpenLinkModal(false);
                        if (invoice) {
                            setSelectedInvoiceId(Number(invoice.id));
                            setOpenModal('edit');
                        }
                    }}
                    companyStatus={{ lowestAmount: null, highestAmount: null }}
                />
            )}
            <Topbar
                title={
                    // Same treatment as the Provisions page - the search box
                    // doesn't fit next to the title alongside UserMenu's own
                    // fixed width on mobile, so it moves to its own full-width
                    // row below instead (see the isMobile block right after
                    // this Topbar). The count badge stays right next to the
                    // title itself; the amount drops to its own line below
                    // both rather than crowding onto the same line.
                    isMobile ? 'Invoices' : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            Invoices
                            <HeaderSearch
                                value={columnFilters.headerKeyword}
                                onSearch={(v) => setColumnFilters(prev => ({ ...prev, headerKeyword: v || undefined }))}
                                placeholder="Search invoices..."
                                width={340}
                                endAdornment={lineItemToggle}
                            />
                            {totalInvoicesCount != null && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: 16,
                                        fontWeight: 600,
                                        background: 'var(--color-container-background)',
                                        color: 'var(--color-text-muted)',
                                    }}
                                >
                                    {totalInvoicesCount}
                                </Box>
                            )}
                            {totalInvoicesAmount != null && (
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: 16,
                                        fontWeight: 600,
                                        background: 'var(--color-container-background)',
                                        color: 'var(--color-text-muted)',
                                    }}
                                >
                                    {formatAmount ? formatAmount(totalInvoicesAmount) : totalInvoicesAmount}
                                </Box>
                            )}
                        </Box>
                    )
                }
                isMobile={isMobile}
                mobileMenuButton={
                    isMobile && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            onClick={() => setSidebarOpen(true)} 
                            sx={{ 
                                color: '#fff', 
                                height: 40,
                                display: 'flex',
                                gap: 1,
                                minWidth: 40,
                                p: 1,
                                backgroundColor: 'var(--color-primary)', 
                                '&:hover': { backgroundColor: 'var(--color-primary)' } 
                            }}
                        >
                            <Menu size={20} color="#fff"/>
                        </Button>
                    )
                }
            >
                {!isMobile && (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%', justifyContent: 'flex-end' }}>
                        <XeroSyncButton type="invoices" />
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!can('invoices.import', 'create')}
                            onClick={() => setOpenModal('import')}
                            sx={{
                                color: 'var(--color-primary)',
                                height: 35,
                                display: 'flex',
                                gap: 1,
                                border: '1px solid var(--color-primary)',
                                backgroundColor: 'var(--color-page-background)',
                                '&:hover': { backgroundColor: 'rgba(var(--color-primary-rgb), 0.0863)' }
                            }}
                        >
                            <Upload size={16} color="var(--color-primary)"/>
                            Import
                        </Button>
                        <AddBtn disabled={!canCreate} onClick={() => setOpenModal('add')} label="Add New Invoice" />
                        <IconButton
                            size="small"
                            onClick={(e) => setSettingsMenuAnchor(e.currentTarget)}
                            title="Invoice tools"
                            sx={{ color: 'var(--color-primary)' }}
                        >
                            <Settings size={18} />
                        </IconButton>
                        <MuiMenu
                            anchorEl={settingsMenuAnchor}
                            open={!!settingsMenuAnchor}
                            onClose={() => setSettingsMenuAnchor(null)}
                        >
                            <MenuItem
                                disabled={!canUpdate || recalculateProvisions.isLoading}
                                onClick={() => { setSettingsMenuAnchor(null); handleRecalculateProvisions(); }}
                                title="Recreate or refresh every invoice's provision transaction using the same date/amount logic as when it's first generated - provisions already linked to a paid transaction are left alone"
                                sx={{ fontSize: 14 }}
                            >
                                {recalculateProvisions.isLoading ? 'Recalculating…' : 'Recalculate Provisions'}
                            </MenuItem>
                        </MuiMenu>
                    </Box>
                )}
            </Topbar>
            {isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 1, position: 'sticky', top: 77, zIndex: 2, background: 'var(--color-page-background)' }}>
                    <HeaderSearch
                        value={columnFilters.headerKeyword}
                        onSearch={(v) => setColumnFilters(prev => ({ ...prev, headerKeyword: v || undefined }))}
                        placeholder="Search invoices..."
                        width="100%"
                        endAdornment={lineItemToggle}
                    />
                    {totalInvoicesCount != null && (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: 16,
                                fontWeight: 600,
                                background: 'var(--color-page-background)',
                                color: 'var(--color-text-muted)',
                                flexShrink: 0,
                            }}
                        >
                            {totalInvoicesCount}
                        </Box>
                    )}
                    {totalInvoicesAmount != null && (
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: 16,
                                fontWeight: 600,
                                background: 'var(--color-page-background)',
                                color: 'var(--color-text-muted)',
                                flexShrink: 0,
                            }}
                        >
                            {formatAmount ? formatAmount(totalInvoicesAmount) : totalInvoicesAmount}
                        </Box>
                    )}
                    <Box
                        onClick={() => setCombinedFilterOpen(true)}
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, borderRadius: 1, cursor: 'pointer', flexShrink: 0,
                        }}
                    >
                        <SlidersHorizontal size={20} color="var(--color-primary)" />
                    </Box>
                </Box>
            )}
            <div style={{ padding: '10px 10px 0' }}>
                {/* Invoices per period - closed until opened, like the Documents
                    table's page chart. Follows the table's filters. */}
                <Paper sx={{ borderRadius: 0, px: { xs: 1.5, sm: 4 }, py: 1.5 }}>
                    <Box
                        onClick={() => setShowPeriodChart(v => !v)}
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}
                    >
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Invoices per period</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {showPeriodChart && (
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={periodGrain}
                                    onClick={e => e.stopPropagation()}
                                    onChange={(_, v) => v && setPeriodGrain(v)}
                                    sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1.25, fontSize: 12, textTransform: 'none' } }}
                                >
                                    <ToggleButton value="month">Month</ToggleButton>
                                    <ToggleButton value="quarter">Quarter</ToggleButton>
                                    <ToggleButton value="year">Year</ToggleButton>
                                </ToggleButtonGroup>
                            )}
                            {showPeriodChart ? <ChevronUp size={18} color="var(--color-primary)" /> : <ChevronDown size={18} color="var(--color-primary)" />}
                        </Box>
                    </Box>
                    <Collapse in={showPeriodChart} unmountOnExit>
                        <Box sx={{ pt: 1.5 }}>
                            <InvoiceChart
                                rows={periodRows}
                                labelHeader="Period"
                                formatAmount={(n) => (formatAmount ? formatAmount(n) : String(n))}
                            />
                        </Box>
                    </Collapse>
                </Paper>
            </div>
            <div style={{ padding: '10px' }}>
                <DynamicTableVirtual
                    columns={columns}
                    data={displayedData}
                    isLoading={fetch.isLoading && !isLoadingMoreInvoices}
                    scrollMode="page"
                    additionalActions={additionalActions}
                    canEditRows={canUpdate}
                    canDeleteRows={canDelete}
                    hasNextPage={hasMoreInvoices}
                    onLoadMore={handleLoadMoreInvoices}
                    isLoadingMore={isLoadingMoreInvoices}
                    sortState={sortBy ? { column: sortColumnIdFor(sortBy), direction: sortOrder === 'asc' ? 'ASC' : 'DESC' } : undefined}
                    hideMobileFilterBar
                    // Opens the invoice's transaction details (history, links,
                    // OPEN/CLOSED) - the same popup a transaction row opens.
                    // Editing the invoice itself is the row's "..." > Edit.
                    onRowClick={(row) => { setAutoOpenLinkModal(false); setLinkInvoiceNumber(row.invoiceNumber); }}
                />
            </div>
            <SidebarMobileDrawer
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
            />
            {isMobile && (
                <BottomSheetDialog open={combinedFilterOpen} onClose={() => setCombinedFilterOpen(false)} heightVh={88}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 1, flexShrink: 0, borderBottom: '1px solid #eee' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Sort & Filter</Typography>
                        <IconButton size="small" onClick={() => setCombinedFilterOpen(false)}>
                            <X size={20} />
                        </IconButton>
                    </Box>
                    <Box sx={{ px: 2, py: 2, overflowY: 'auto', flex: 1 }}>
                        <CombinedFilterSection title="ID" section="invoiceNumber">
                            <DescriptionFilter
                                ref={invoiceNumberFilterRef}
                                embedded
                                hideActions
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentDescriptionKeyword={columnFilters.invoiceNumberKeyword}
                                fieldLabel="ID"
                                sortByValue="invoiceNumber"
                                placeholder="search ID"
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Status" section="status">
                            <StatusFilter
                                ref={statusFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                options={statuses.map(s => {
                                    const breakdown = statusBreakdown[String(s.value)];
                                    return {
                                        id: String(s.value),
                                        label: s.label,
                                        count: breakdown?.count ?? 0,
                                        totalAmount: formatAmount ? formatAmount(breakdown?.totalAmount ?? 0) : String(breakdown?.totalAmount ?? 0),
                                    };
                                })}
                                selectedIds={selectedStatusIds || []}
                                sortByValue="status"
                                sortFieldLabel="Status"
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                onApply={(result) => {
                                    setSelectedStatusIds(result.selectedIds);
                                    if (result.sortBy) handleCombinedFilterDraftApply({ sortBy: result.sortBy, sortDirection: result.sortDirection });
                                }}
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Invoice Date" section="invoiceDate">
                            <SimpleDateFilter
                                ref={invoiceDateFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentStartDate={columnFilters.invoiceDateStart}
                                currentEndDate={columnFilters.invoiceDateEnd}
                                fieldLabel="Invoice Date"
                                sortByValue="issueDate"
                                defaultRangeStart={invoiceDateBounds.min}
                                defaultRangeEnd={invoiceDateBounds.max}
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Provision Date" section="provisionDate">
                            <SimpleDateFilter
                                ref={provisionDateFilterRef}
                                open={false}
                                anchorEl={null}
                                onClose={() => {}}
                                embedded
                                hideActions
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentStartDate={columnFilters.provisionDateStart}
                                currentEndDate={columnFilters.provisionDateEnd}
                                fieldLabel="Provision Date"
                                sortByValue="provisionDate"
                                defaultRangeStart={provisionDateBounds.min}
                                defaultRangeEnd={provisionDateBounds.max}
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Amount" section="amount">
                            <AmountFilter
                                ref={amountCombinedFilterRef}
                                embedded
                                hideActions
                                min={amountBounds.min}
                                max={amountBounds.max}
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy: sortBy === 'total' ? 'amount' : sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentAmountKeyword={columnFilters.amountKeyword}
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Company Name" section="company">
                            <DescriptionFilter
                                ref={companyFilterRef}
                                embedded
                                hideActions
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentDescriptionKeyword={columnFilters.companyKeyword}
                                fieldLabel="Company Name"
                                sortByValue="clientCompanyName"
                                placeholder="search company name"
                            />
                        </CombinedFilterSection>
                        <CombinedFilterSection title="Email" section="email">
                            <DescriptionFilter
                                ref={emailFilterRef}
                                embedded
                                hideActions
                                onApply={handleCombinedFilterDraftApply}
                                currentSort={{ sortBy, sortDirection: sortOrder === 'asc' ? 'ASC' : 'DESC' }}
                                currentDescriptionKeyword={columnFilters.emailKeyword}
                                fieldLabel="Email"
                                sortByValue="clientEmail"
                                placeholder="search email"
                            />
                        </CombinedFilterSection>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 1.5, flexShrink: 0, borderTop: '1px solid #eee' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}
                            onClick={handleApplyAllCombinedFilters}
                        >
                            Apply Now
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                            onClick={handleClearAllCombinedFilters}
                        >
                            Clear
                        </Button>
                    </Box>
                </BottomSheetDialog>
            )}
            {isMobile && (
                <>
                    <Fab
                        onClick={(e) => setFabMenuAnchor(e.currentTarget)}
                        sx={{
                            position: 'fixed',
                            bottom: 20,
                            right: 20,
                            background: 'var(--color-primary)',
                            color: '#fff',
                            '&:hover': { background: 'var(--color-primary-hover)' },
                            zIndex: 999,
                        }}
                    >
                        <Plus size={24} />
                    </Fab>
                    <MuiMenu
                        anchorEl={fabMenuAnchor}
                        open={Boolean(fabMenuAnchor)}
                        onClose={() => setFabMenuAnchor(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                        <MenuItem
                            disabled={!canCreate}
                            onClick={() => { setOpenModal('add'); setFabMenuAnchor(null); }}
                            sx={{ display: 'flex', gap: 1 }}
                        >
                            <Plus size={16} color="var(--color-primary)" />
                            Add New Invoice
                        </MenuItem>
                        <MenuItem
                            disabled={!can('invoices.import', 'create')}
                            onClick={() => { setOpenModal('import'); setFabMenuAnchor(null); }}
                            sx={{ display: 'flex', gap: 1 }}
                        >
                            <Upload size={16} color="var(--color-primary)" />
                            Import
                        </MenuItem>
                    </MuiMenu>
                </>
            )}
        </div>
    );
};

export default Invoice;